import { memo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiDatePicker,
  MiDetailFields,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  INPUT_TYPES,
  MODAL_TITLES,
  PRIORITY_OPTIONS,
  SECTION_HEADER_VARIANTS,
  TIME_PREFERENCE_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { toFormattedDate } from "@miplatform/utils";
import {
  mdiCancel,
  mdiContentSave,
  mdiInformationOutline,
  mdiPencil,
} from "@mdi/js";
import { AddressDetailFields, Footer } from "@mibuildweb/components";
import { Box } from "@mui/material";

const PLACEHOLDER = "--";

export const CloseoutDetails = memo(
  ({ home, orderNotes, orderInfo, orderId, updateOrder, orderModifiedOn }) => {
    const { classes, theme } = useStyles();

    const [readOnly, setReadOnly] = useState(true);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);

    const orderEdit = useEdit({
      ...orderInfo,
      orderNotes: orderNotes ?? PLACEHOLDER,
    });

    const orderDetailsFields = [
      {
        label: "Requested Date",
        fieldType: INPUT_TYPES.CUSTOM,
        name: "orderRequestDate",
        readOnly,
        customComponent: (
          <MiDatePicker
            isFullWidth
            label="Requested Date"
            onChange={(value) => {
              orderEdit.update({
                orderRequestDate: toFormattedDate(value),
              });
            }}
            variant={DATEPICKER_VARIANTS.DESKTOP}
            actions={[DATEPICKER_ACTIONS.CANCEL]}
          />
        ),
        style: {
          paddingLeft: 2,
          paddingRight: 2,
        },
        width: "30%",
      },
      {
        label: "Time Preference",
        name: "orderTimePreference",
        fieldType: INPUT_TYPES.SELECT,
        options: TIME_PREFERENCE_OPTIONS,
        readOnly,
        InputLabelProps: {
          shrink: true,
        },
        style: {
          paddingLeft: 2,
          paddingRight: 2,
        },
        handleChange: ({ target: { value } }) => {
          orderEdit.update({ orderTimePreference: value });
        },
        width: "30%",
      },
      {
        label: "Priority",
        name: "orderPriority",
        fieldType: INPUT_TYPES.SELECT,
        options: PRIORITY_OPTIONS,
        readOnly,
        width: "30%",
        style: {
          paddingLeft: 2,
          paddingRight: 2,
        },
      },
      {
        label: "Notes",
        name: "orderNotes",
        multiline: true,
        rows: 3,
        readOnly,
        style: {
          paddingLeft: 2,
          paddingRight: 2,
        },
      },
    ];

    return (
      <Box className={classes.root}>
        <AddressDetailFields home={home} />
        <Box className={classes.section}>
          <MiDetailFields
            headerProps={{
              title: "Order Information",
              icon: mdiInformationOutline,
              variant: SECTION_HEADER_VARIANTS.UNDERLINED,
              rightIcon: readOnly ? mdiPencil : undefined,
              onRightIconClick: () => setReadOnly(false),
            }}
            detailFields={orderDetailsFields}
            edit={orderEdit}
          />
        </Box>
        <Footer
          isDirty={orderEdit.isDirty()}
          show={!readOnly}
          handleSaveClick={() => setOpenConfirmModal(true)}
          cancelTitleIcon={{ path: mdiCancel, color: theme.palette.red.main }}
          handleCancelClick={() => {
            orderEdit.reset({
              ...orderInfo,
              orderNotes: orderNotes ?? PLACEHOLDER,
            });
            setReadOnly(true);
          }}
          disableSave={!orderEdit.isDirty()}
        />
        <MiModalConfirm
          title={MODAL_TITLES.SAVE}
          titleIcon={{
            path: mdiContentSave,
          }}
          textPositive="Yes, Save"
          open={openConfirmModal}
          handleNegative={() => setOpenConfirmModal(false)}
          handlePositive={() => {
            updateOrder({
              orderId,
              data: orderEdit.edits,
              orderModifiedOn,
            });
            setOpenConfirmModal(false);
            setReadOnly(true);
          }}
          description="You are about to save changes to the order details. Are you sure you would like to continue?"
          buttonPositiveColor={BUTTON_COLORS.PRIMARY}
        />
      </Box>
    );
  }
);
const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(4),
  },
  orderNotes: {
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.typography.pxToRem(14),
  },
  orderNotesField: {
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.common.black,
      color: theme.palette.common.black,
    },
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    margin: theme.spacing(0, 1),
  },
  contentContainer: {
    display: "flex",
    justifyContent: "space-between",
    margin: theme.spacing(0, 4),
  },
}));
